class TestimonialsSlider extends HTMLElement {
  constructor() {
    super();
    this.track = this.querySelector('#testimonials-track');
    this.prevBtn = this.querySelector('#testimonials-prev');
    this.nextBtn = this.querySelector('#testimonials-next');
    this.slides = this.querySelectorAll('.testimonials-slider__slide');
    this.currentIndex = 0;
    this.slideWidth = 0;
    this.visibleSlides = 1;
    this.maxIndex = 0;
    // Drag state
    this.isDragging = false;
    this.startX = 0;
    this.startY = 0;
    this.hasMoved = false;
    this.startTranslate = 0;
    this.lastDeltaX = 0;
    
    this.init();
  }

  init() {
    if (!this.track || !this.slides.length) return;
    
    this.calculateSlideWidth();
    this.setupEventListeners();
    this.updateNavigation();

    // Disable native drag on images inside the slider
    this.querySelectorAll('img').forEach((img) => {
      try { img.setAttribute('draggable', 'false'); } catch(_) {}
      img.addEventListener('dragstart', (e) => e.preventDefault());
    });
    // Defensive: prevent dragstart bubbling from any element
    this.addEventListener('dragstart', (e) => e.preventDefault());
    
    // Handle window resize
    window.addEventListener('resize', () => {
      this.calculateSlideWidth();
      this.updateSliderPosition();
      this.updateNavigation();
    });
  }

  calculateSlideWidth() {
    const containerWidth = this.track.parentElement.offsetWidth;
    const gap = 16; // 16px gap between slides
    
    // Calculate how many slides can fit
    if (window.innerWidth <= 768) {
      // Mobile: show 1 slide at a time
      this.visibleSlides = 1;
      this.slideWidth = Math.min(295, containerWidth - gap);
    } else if (window.innerWidth <= 1024) {
      // Tablet: show 2 slides
      this.visibleSlides = 2;
      this.slideWidth = (containerWidth - gap) / 2;
    } else {
      // Desktop: show 3 slides
      this.visibleSlides = 3;
      this.slideWidth = (containerWidth - gap) / 3;
    }
    
    // Ensure slide width doesn't exceed max width
    this.slideWidth = Math.min(this.slideWidth, 396);
    
    // Calculate max index
    this.maxIndex = Math.max(0, this.slides.length - this.visibleSlides);
    
    // Update slide widths
    this.slides.forEach(slide => {
      slide.style.width = `${this.slideWidth}px`;
    });
  }

  setupEventListeners() {
    if (this.prevBtn) {
      this.prevBtn.addEventListener('click', () => this.previousSlide());
    }
    
    if (this.nextBtn) {
      this.nextBtn.addEventListener('click', () => this.nextSlide());
    }

    // Pointer/touch drag support on the track
    if (this.track) {
      const onPointerDown = (e) => {
        // Left mouse or touch/pen
        if (e.pointerType === 'mouse' && e.button !== 0) return;
        this.isDragging = true;
        this.hasMoved = false;
        this.startX = e.clientX ?? (e.touches ? e.touches[0].clientX : 0);
        this.startY = e.clientY ?? (e.touches ? e.touches[0].clientY : 0);
        this.startTranslate = -(this.currentIndex * (this.slideWidth + 16));
        this.lastDeltaX = 0;
        // Disable transition while dragging
        this.track.style.transition = 'none';
        // Add dragging class to prevent text selection
        this.classList.add('testimonials-slider--dragging');
        try { this.track.setPointerCapture && this.track.setPointerCapture(e.pointerId); } catch(_) {}
      };

      const onPointerMove = (e) => {
        if (!this.isDragging) return;
        const x = e.clientX ?? (e.touches ? e.touches[0].clientX : 0);
        const y = e.clientY ?? (e.touches ? e.touches[0].clientY : 0);
        const deltaX = x - this.startX;
        const deltaY = y - this.startY;
        // If horizontal intent, prevent page scroll
        if (Math.abs(deltaX) > Math.abs(deltaY)) {
          e.preventDefault && e.preventDefault();
        }
        this.hasMoved = Math.abs(deltaX) > 3;
        this.lastDeltaX = deltaX;
        const translate = this.startTranslate + deltaX;
        this.track.style.transform = `translateX(${translate}px)`;
      };

      const onPointerUp = (e) => {
        if (!this.isDragging) return;
        this.isDragging = false;
        // Restore transition
        this.track.style.transition = '';
        // Remove dragging class
        this.classList.remove('testimonials-slider--dragging');
        const threshold = Math.max(40, Math.round(this.slideWidth * 0.15));
        let targetIndex = this.currentIndex;
        if (this.lastDeltaX <= -threshold) {
          targetIndex = Math.min(this.currentIndex + 1, this.maxIndex);
        } else if (this.lastDeltaX >= threshold) {
          targetIndex = Math.max(this.currentIndex - 1, 0);
        }
        this.currentIndex = targetIndex;
        this.updateSliderPosition();
        this.updateNavigation();
        try { this.track.releasePointerCapture && e && this.track.releasePointerCapture(e.pointerId); } catch(_) {}
      };

      // Use Pointer Events when available
      const usePointer = 'PointerEvent' in window;
      if (usePointer) {
        this.track.addEventListener('pointerdown', onPointerDown);
        this.track.addEventListener('pointermove', onPointerMove, { passive: false });
        this.track.addEventListener('pointerup', onPointerUp);
        this.track.addEventListener('pointercancel', onPointerUp);
        this.track.addEventListener('pointerleave', onPointerUp);
      } else {
        // Fallback to touch/mouse
        this.track.addEventListener('touchstart', onPointerDown, { passive: true });
        this.track.addEventListener('touchmove', onPointerMove, { passive: false });
        this.track.addEventListener('touchend', onPointerUp);
        this.track.addEventListener('mousedown', onPointerDown);
        window.addEventListener('mousemove', onPointerMove, { passive: false });
        window.addEventListener('mouseup', onPointerUp);
      }
    }
  }

  nextSlide() {
    if (this.currentIndex < this.maxIndex) {
      this.currentIndex++;
      this.updateSliderPosition();
      this.updateNavigation();
    }
  }

  previousSlide() {
    if (this.currentIndex > 0) {
      this.currentIndex--;
      this.updateSliderPosition();
      this.updateNavigation();
    }
  }

  updateSliderPosition() {
    const translateX = -(this.currentIndex * (this.slideWidth + 16)); // 16px gap
    this.track.style.transform = `translateX(${translateX}px)`;
  }

  updateNavigation() {
    // Update prev button
    if (this.prevBtn) {
      this.prevBtn.disabled = this.currentIndex === 0;
    }
    
    // Update next button
    if (this.nextBtn) {
      this.nextBtn.disabled = this.currentIndex >= this.maxIndex;
    }
  }
}

// Initialize the component
customElements.define('testimonials-slider-component', TestimonialsSlider);
